import { defineMessages } from 'react-intl';

/**
 * Trial-extension checklist copy that varies with the NUMBER of tasks.
 *
 * Only the count-bearing strings live here. The rest of the checklist copy is
 * still i18next (`trialExtension.*` in `src/locales/en/common.json`) because it
 * is count-agnostic and its translations are live in 19 locales — migrating a
 * string forks a new message id and reverts it to English until Crowdin catches
 * up, so it is done only when the English itself has to change.
 *
 * These three had to change: the two `*NotStarted` strings hardcoded the
 * literal "3" (true for every checklist until GRO-4364 added a 4th task to
 * CLINICIAN_PRO), and their locale JSON is Crowdin-owned and cannot be
 * hand-edited. `modalBodyProgress` is new — it fills the mid-progress gap that
 * only opens at 4+ tasks.
 *
 * "14 days" stays hardcoded here exactly as it is in the i18next catalog; the
 * extension length is not parameterized anywhere in this surface.
 *
 * New checklist English also lands here, since the i18next catalog is closed to
 * new keys.
 */
export const trialExtensionProgressMessages = defineMessages({
  modalBodyNotStarted: {
    defaultMessage:
      'Explore what Heidi can do — complete all tasks and we’ll automatically add 14 days to your {tier} trial.',
    id: 'Br+GbvbrPF',
    description:
      'Trial extension modal body when no checklist task is complete yet. {tier} is the plan name, e.g. Clinician, Practice, Evidence.',
  },
  modalBodyProgress: {
    defaultMessage:
      '{completed} of {total} done — finish the rest and we’ll add 14 days to your {tier} trial.',
    id: 'PV8U5QYgsP',
    description:
      'Trial extension modal body when at least one checklist task is complete but more than one remains. {tier} is the plan name, e.g. Clinician, Practice, Evidence.',
  },
  modalBodyActionsStartedHeidi2Clinician: {
    defaultMessage:
      'Extend your free Clinician trial and unlimited usage by completing the below steps.',
    id: 'Xf8su/T6Tj',
    description:
      'Trial extension modal body for a Heidi 2.0 Clinician trial user before every checklist task is complete. The trial comes with unlimited usage.',
  },
  modalTitleEarnHeidi2Clinician: {
    defaultMessage: 'Earn {days} free days',
    id: 'f784c62DqY',
    description:
      'Trial extension modal title for a Heidi 2.0 Clinician trial user before every checklist task is complete. {days} is extra trial days granted on completion.',
  },
  modalTitleEarnedHeidi2Clinician: {
    defaultMessage: 'You’ve earned {days} free days!',
    id: 'mDeaDX+5QH',
    description:
      'Trial extension modal title after a Heidi 2.0 Clinician trial user finishes every checklist task. {days} is extra trial days granted on completion.',
  },
  modalBodyEarnedHeidi2Clinician: {
    defaultMessage:
      'You’ve extended your free Clinician trial by {days, plural, one {# day} other {# days}}.',
    id: 'vW35OH/5R9',
    description:
      'Trial extension modal body after a Heidi 2.0 Clinician trial user finishes every checklist task. {days} is extra trial days granted on completion.',
  },
  modalUpgradeCtaHeidi2Clinician: {
    defaultMessage: 'Upgrade now',
    id: 'MHgYDUZtBe',
    description:
      'Trial extension modal primary button after a Heidi 2.0 Clinician trial user finishes every checklist task. Opens checkout for the Clinician plan.',
  },
  hoverCardSubtitleNotStarted: {
    defaultMessage: 'Try Heidi’s features — we’ll add 14 days to your trial.',
    id: 'YbuB23jPqr',
    description:
      'Sidebar trial-extension hover card subtitle when no checklist task is complete and no action has been started',
  },
  hoverCardSubtitleNotStartedHeidi2Clinician: {
    defaultMessage:
      'Complete {count, plural, one {# step} other {# steps}} to extend your free trial of Clinician and unlimited usage.',
    id: '5tuL76L8my',
    description:
      'Sidebar trial-extension hover card subtitle for a Heidi 2.0 Clinician trial user who has not started any checklist task. {count} is the number of checklist tasks. The trial comes with unlimited usage.',
  },
  hoverCardSubtitleCompleteHeidi2Clinician: {
    defaultMessage:
      '{days, plural, one {# day has} other {# days have}} been automatically added to your Clinician trial. We’ll let you know when your trial ends.',
    id: 'R6+gGjga9H',
    description:
      'Hover-card subtitle and modal footer after a Heidi 2.0 Clinician trial user finishes every checklist task. {days} is extra trial days granted on completion.',
  },
  hoverCardCtaRemainingHeidi2Clinician: {
    defaultMessage: 'View remaining steps',
    id: 'KEP6lkpY3T',
    description:
      'Sidebar trial-extension hover card CTA after a Heidi 2.0 Clinician trial user has started any checklist step.',
  },
  hoverCardCtaCloseHeidi2Clinician: {
    defaultMessage: 'Close',
    id: 'pnrTXn48D+',
    description:
      'Sidebar trial-extension hover card ghost button that dismisses the gift nav item after a Heidi 2.0 Clinician trial user finishes every checklist task.',
  },
  modalFooterNoActionHeidi2Clinician: {
    defaultMessage:
      'Once completed, an additional 14 days will be added automatically to the remaining days of your initial trial period.',
    id: 'SdoUGKmo3u',
    description:
      'Trial extension modal footer for a Heidi 2.0 Clinician trial user before every checklist task is complete. The extra days attach to the remaining initial trial with no further action.',
  },
});
